/*
 * A text file drawn by the page itself: the drive's file page and a chat attachment's *Preview* both
 * show one this way (security.md §18 R34; DV S-06 §3.5).
 *
 * The file is never framed. A frame of the byte route would put a person's file into the one list of
 * frames the page's policy allows, which holds only the render frame and no user data, so the page
 * fetches the byte route's inline answer — `text/plain; charset=utf-8` under the sandbox policy, at
 * most the inline cap — and puts the words into one text node of a `<pre>`. Text is never parsed as
 * markup here: a file holding `<script>` shows those characters, as an editor would. What the reader
 * gets for it is a sheet in the app's own theme, selectable, and found by the browser's search.
 *
 * The sheet is a named scroll region the keyboard reaches, the treatment a code block has: a long
 * file scrolls inside it and has to be readable without a mouse. A fetch that fails says so in the
 * sheet; the host keeps its *Download* beside it whatever happens here.
 *
 * A long file is cut. Drawing a text is one layout of the whole of it, and the page does nothing else
 * meanwhile: measured in Chromium at a page's measure, 8 MiB of log lines took 0.5 s at full speed
 * and 2.1 s with the CPU slowed four times, one 8 MiB line 0.8 and 3.3 s, while 1 MiB took 0.08–0.1 s
 * and 0.3–0.4 s; a scroll afterwards cost a frame at every size. So the sheet draws the first
 * `FILE_TEXT_MAX_CHARS` characters, ending at the last whole line inside them, and says how many
 * lines it shows; the file itself is the download's.
 */

import { useEffect, useState } from "react"
import { cx } from "../../lib/cx.ts"
import { formatNumber } from "../i18n/format.ts"
import { t } from "../i18n/index.ts"
import "./fileText.css"

/** The most characters a sheet draws: the measured size at which drawing stops being a stall. */
export const FILE_TEXT_MAX_CHARS = 1 << 20

/** What a sheet draws of a file's text: the words, how many lines they are, and whether it is cut. */
export interface ShownText {
	text: string
	lines: number
	cut: boolean
}

/**
 * The part of `whole` a sheet draws: all of it when it fits in `max` characters, else the lines that
 * fit whole — or, when even the first line does not, its first `max` characters, never half of a
 * character that takes two code units. A trailing line break starts no line of its own.
 */
export function shownText(whole: string, max: number = FILE_TEXT_MAX_CHARS): ShownText {
	if (whole.length <= max) return { text: whole, lines: linesOf(whole), cut: false }
	const head = whole.slice(0, max)
	const lastBreak = head.lastIndexOf("\n")
	let text = lastBreak > 0 ? head.slice(0, lastBreak) : head
	if (lastBreak <= 0 && /[\uD800-\uDBFF]$/.test(text)) text = text.slice(0, -1)
	return { text, lines: linesOf(text), cut: true }
}

function linesOf(text: string): number {
	if (text === "") return 0
	let breaks = 0
	for (let at = text.indexOf("\n"); at !== -1; at = text.indexOf("\n", at + 1)) breaks++
	return text.endsWith("\n") ? breaks : breaks + 1
}

type Sheet = { state: "loading" } | { state: "failed" } | { state: "shown"; shown: ShownText }

/** What one address answered. A sheet asked for another address is loading until that one answers. */
interface Answered {
	src: string
	sheet: Sheet
}

export interface FileTextProps {
	/** The byte route's inline address of the file (`…?d=inline`). */
	src: string
	/** The file's name, which names the region. */
	name: string
	className?: string
}

export function FileText({ src, name, className }: FileTextProps) {
	const [answered, setAnswered] = useState<Answered | null>(null)
	const sheet: Sheet = answered?.src === src ? answered.sheet : { state: "loading" }

	useEffect(() => {
		const abort = new AbortController()
		fetch(src, { credentials: "same-origin", signal: abort.signal })
			.then(async (response) => {
				if (!response.ok) throw new Error(`the byte route answered ${String(response.status)}`)
				return response.text()
			})
			.then(
				(text) => setAnswered({ src, sheet: { state: "shown", shown: shownText(text) } }),
				() => {
					// A sheet that left the screen asked for nothing more: its abort is not a failure.
					if (!abort.signal.aborted) setAnswered({ src, sheet: { state: "failed" } })
				},
			)
		return () => abort.abort()
	}, [src])

	return (
		<div className={cx("tb-file-text", className)} aria-busy={sheet.state === "loading"}>
			{sheet.state === "failed" ? (
				<p className="tb-file-text__failed" role="alert">
					{t("render.file.failed")}
				</p>
			) : null}
			{sheet.state === "shown" && sheet.shown.cut ? (
				<p className="tb-file-text__partial">
					{sheet.shown.lines === 1
						? t("render.file.partial.one")
						: t("render.file.partial", { count: formatNumber(sheet.shown.lines) })}
				</p>
			) : null}
			{sheet.state === "shown" ? (
				<pre
					className="tb-file-text__body"
					role="region"
					// A region that scrolls is a stop the keyboard lands on, or its overflow is
					// unreadable without a mouse — the code block's reason (CodeBlock.tsx).
					// eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
					tabIndex={0}
					aria-label={t("render.file.region", { name })}
				>
					{sheet.shown.text}
				</pre>
			) : null}
		</div>
	)
}
